import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag, X } from "lucide-react";
import { useAllFeatureFlags } from "@/lib/feature-flags";
import { getFeatureFlag } from "@/lib/feature-flags/registry";
import { useIsSuperuser } from "@/lib/rbac/hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";

/**
 * Superuser-only debug banner listing the feature flags / workspace preferences
 * that have been EXPLICITLY activated for the current workspace.
 *
 * The workspace `preferences` JSON only contains keys that were explicitly set
 * for this workspace — absent keys fall back to the registry `defaultValue`. So
 * every truthy entry here is a real per-workspace override, which is exactly the
 * information a superuser needs to understand why a workspace behaves
 * differently from the defaults. Registry `defaultValue: true` flags do NOT show
 * up unless a workspace admin explicitly toggled them.
 *
 * Only rendered for superusers; invisible to regular users (and in Lovable
 * preview, where `isSuperuser` is false). Dismissible for the session, keyed by
 * workspace so switching workspaces re-surfaces it.
 */

export interface ActivatedPreference {
  /** Raw preference / feature-flag key as stored in the preferences JSON */
  key: string;
  /** Non-boolean values are shown inline (e.g. `key: "value"`); true → undefined */
  value?: string;
  /** Friendly registry name + description, surfaced as a tooltip */
  description?: string;
}

/**
 * Reduce the parsed workspace preferences into the list of meaningfully
 * overridden entries.
 *
 * Two filters apply:
 *  - only truthy values count as "activated"; explicitly-disabled (false)
 *    overrides are omitted.
 *  - registered flags whose explicit value merely matches the registry
 *    `defaultValue` are omitted — they carry no information beyond the
 *    out-of-the-box behavior (e.g. `lead-finder` defaults to true, so an
 *    explicit `true` is noise). Only genuine deviations from the default, and
 *    keys with no registry default at all, are surfaced.
 */
export function getActivatedPreferences(
  preferences: Record<string, unknown>
): ActivatedPreference[] {
  return Object.entries(preferences)
    .filter(([key, value]) => {
      if (!value) return false;
      const flag = getFeatureFlag(key);
      // Drop no-op overrides that just restate the registry default.
      if (flag && value === flag.defaultValue) return false;
      return true;
    })
    .map(([key, value]) => {
      const flag = getFeatureFlag(key);
      return {
        key,
        value: value === true ? undefined : formatValue(value),
        description: flag ? `${flag.name} — ${flag.description}` : undefined,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export default function SuperuserFlagsBanner() {
  const { t } = useTranslation();
  const isSuperuser = useIsSuperuser();
  const allFlags = useAllFeatureFlags();
  const workspaceId = useWorkspaceId();
  // Track which workspace was dismissed so switching workspaces re-surfaces it.
  const [dismissedWorkspace, setDismissedWorkspace] = useState<string | null>(null);

  const activated = useMemo(() => getActivatedPreferences(allFlags), [allFlags]);

  if (
    !isSuperuser ||
    dismissedWorkspace === (workspaceId ?? "none") ||
    activated.length === 0
  ) {
    return null;
  }

  return (
    <div className="relative w-full shrink-0 border-b border-violet-500/30 bg-violet-500/10 px-4 py-1 dark:bg-violet-500/15 dark:border-violet-500/40">
      <div className="flex items-center gap-3 pr-8">
        <div className="flex items-center gap-1.5 shrink-0 text-violet-600 dark:text-violet-300">
          <Flag className="h-3.5 w-3.5" strokeWidth={2.5} />
          <span className="text-[11px] font-semibold uppercase tracking-wide">
            {t("banners.superuserActiveFlags")}
          </span>
        </div>
        <div className="flex flex-nowrap items-center gap-1.5 min-w-0 overflow-x-auto scrollbar-none">
          {activated.map((pref) => (
            <span
              key={pref.key}
              title={pref.description ?? pref.key}
              className="inline-flex shrink-0 items-center rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 font-mono text-[11px] font-medium text-violet-700 dark:text-violet-200"
            >
              {pref.key}
              {pref.value !== undefined && (
                <span className="ml-1 font-normal text-violet-600/70 dark:text-violet-300/70">
                  {pref.value}
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissedWorkspace(workspaceId ?? "none")}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-violet-700/60 hover:text-violet-700 hover:bg-violet-500/10 dark:text-violet-200/60 dark:hover:text-violet-100"
        aria-label={t("banners.dismiss")}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
